import React from 'react';
import { Auth } from 'aws-amplify';
import { Drawer } from '@mui/material';
import { usePopoverHandlers } from 'shared/components/user-popover';
import { SiteInfo } from 'shared/components';
import { useToggle } from 'shared/hooks';
import { UserPopoverPartner } from 'shared/components/user-popover/user-popover';
import { usePartnerRelativePath } from 'partner/shared/hooks/use-partner-relative-path';
import { useGetPartnerNotification } from 'partner/hooks/use-get-partner-notifications';
import { useQueryClient } from 'react-query';
import { PartnerAuthenticatedElement } from 'partner/shared/components/partner-authenticated-element/partner-authenticated-element';
import { PolicyGroups } from 'shared/utils/policy-controller';
import { StyledSidebar } from 'shared/components/sidebar/styled-sidebar';
import { StyledSidebarLogo } from 'shared/components/sidebar/styled-sidebar-logo';
import { SidebarSectionTitle } from 'shared/components/sidebar/sidebar-section-title';
import { SidebarSection } from 'shared/components/sidebar/sidebar-section';
import { SidebarSectionList } from 'shared/components/sidebar/sidebar-section-list';
import { SidebarSectionLink } from 'shared/components/sidebar/sidebar-section-link';
import { SidebarProfileSection } from 'shared/components/sidebar/sidebar-profile-section';
import { SidebarToggle } from 'shared/components/sidebar/sidebar-toggle';
import { SiteInfoContainer } from 'shared/components/sidebar/site-info-container';
import { useSidebarProvider } from 'shared/contexts/sidebar-provider';
import { useGetPartnerUserInfo } from 'partner/shared/hooks/use-get-partner-user-info';
import { useSidebarItems } from './sidebar-items';
import { SidebarPartnerName } from './sidebar-partner-name';
import { filterOutSidebarItems } from './filter-out-sidebar-items';
import { LOGO_PATH, SMALL_LOGO_PATH } from '../../config';

export const Sidebar = () => {
  const { isSidebarOpen, handleClose, handleOpen } = useSidebarProvider();
  const [userLabel, setUserLabel] = React.useState('');
  const { id: popoverId, anchorEl } = usePopoverHandlers();
  const generatePath = usePartnerRelativePath();
  const { on: userInfoOpen, toggle: toggleUserInfo } = useToggle();
  const { data } = useGetPartnerNotification();
  const queryClient = useQueryClient();
  const items = useSidebarItems();
  const partnerQuery = useGetPartnerUserInfo();

  const logout = async () => {
    await Auth.signOut();
    queryClient.removeQueries();
  };

  // TODO this might be moved to popover itself?
  React.useEffect(() => {
    let isMounted = true;
    Auth.currentUserInfo().then((res) => {
      if (isMounted && res) {
        setUserLabel(res.attributes?.email ?? '');
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredItems = filterOutSidebarItems({
    sidebarItems: items,
    isAvailabilityVisible: partnerQuery.data?.partner.isAvailabilityVisible,
  });

  return (
    <>
      <StyledSidebar variant="permanent" folded={!isSidebarOpen}>
        <StyledSidebarLogo folded={!isSidebarOpen} to={generatePath(items[0].children[0])}>
          <img src={isSidebarOpen ? LOGO_PATH : SMALL_LOGO_PATH} alt="Level.works" />
        </StyledSidebarLogo>
        <SidebarPartnerName
          isSidebarOpen={isSidebarOpen}
          partnerName={partnerQuery.data?.partner.name ?? ''}
        />
        {filteredItems.map((sidebarItem) => {
          return (
            <SidebarSection key={sidebarItem.key}>
              <SidebarSectionTitle isSidebarOpen={isSidebarOpen}>
                {sidebarItem.name}
              </SidebarSectionTitle>
              <SidebarSectionList>
                {sidebarItem.children.map((child) => {
                  const notificationCount = data?.[child.key] ?? 0;
                  const path = generatePath(child);
                  return (
                    <PartnerAuthenticatedElement
                      key={child.key}
                      roles={'PARTNER'}
                      policyGroup={PolicyGroups.PARTNER}
                    >
                      <SidebarSectionLink
                        {...child}
                        path={path}
                        notificationCount={notificationCount}
                        isSidebarOpen={isSidebarOpen}
                      />
                    </PartnerAuthenticatedElement>
                  );
                })}
              </SidebarSectionList>
            </SidebarSection>
          );
        })}
        <SidebarProfileSection
          isSidebarOpen={isSidebarOpen}
          handleToggleUserInfo={toggleUserInfo}
          handleLogout={logout}
        />
        <SiteInfoContainer isSidebarOpen={isSidebarOpen}>
          <SiteInfo />
        </SiteInfoContainer>
      </StyledSidebar>
      <SidebarToggle
        isSidebarOpen={isSidebarOpen}
        handleClose={handleClose}
        handleOpen={handleOpen}
      />
      <Drawer anchor="right" open={userInfoOpen} onClose={toggleUserInfo}>
        <UserPopoverPartner
          userLabel={userLabel}
          id={popoverId}
          open={!!anchorEl}
          anchorEl={anchorEl}
        />
      </Drawer>
    </>
  );
};
